import { createBaseApi } from '../baseApi';

// Create base delivery API
// Assuming createBaseApi handles defining tagTypes or we add them here
// For example, if createBaseApi doesn't automatically add tagTypes from endpoints:
// const deliveryApi = createBaseApi('deliveryApi', ['Delivery', 'FulfillmentGroup', 'Order', 'ParcelTemplate']);
const deliveryApi = createBaseApi('deliveryApi');

// Enhanced API with delivery endpoints
export const enhancedDeliveryApi = deliveryApi.injectEndpoints({
  // Ensure tagTypes are declared if not handled by createBaseApi
  // This might need to be in the createBaseApi call itself.
  // For RTK Query, tagTypes are usually defined at the root API definition.
  // If your createBaseApi doesn't put them here, this line might be ineffective
  // or needs to be handled in the baseApi.js itself.
  // tagTypes: ['Delivery', 'FulfillmentGroup', 'Order', 'ParcelTemplate'], 
  endpoints: (builder) => ({
    // Endpoint to fetch a single Fulfillment Group with populated data
    getFulfillmentGroup: builder.query({
      query: (fulfillmentGroupId) => ({
        url: `fulfillment-groups/${fulfillmentGroupId}`,
        // Re-enabling populate, as the backend controller now handles it.
        // Using the simpler version first, assuming the backend controller
        // will populate nested relations correctly.
        params: {
          _populate: [
            'order',
            'order.users_permissions_user', // For customer details
            'order.users_permissions_user.profilePic', // For customer profile picture
            'order.shippingAddress',
            'shippingOriginAddress',
            'orderItems',
            'orderItems.product', // For product details like category
            'merchant', // For certify_signer name
            'merchant.profilePic', // For merchant profile picture
            'customer', // For customer details
            'customer.profilePic', // For customer profile picture
            'outboundDeliveries', // For checking existing labels
            'returnDeliveries', // For return shipping labels and costs
            'shippingRate',
            'subGroups', // Include sub-groups
            'subGroups.orderItems', // Include order items within sub-groups
            'subGroups.orderItems.product' // Include products within sub-group order items
          ].join(','),
        },
      }),
      providesTags: (result, error, id) => [{ type: 'FulfillmentGroup', id }],
    }),

    // Fetch multiple fulfillment groups
    getMultipleFulfillmentGroups: builder.query({
      async queryFn(fulfillmentGroupIds, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const results = await Promise.all(
            fulfillmentGroupIds.map(id => fetchWithBQ({
              url: `fulfillment-groups/${id}`,
              params: {
                _populate: [
                  'order',
                  'order.users_permissions_user',
                  'order.users_permissions_user.profilePic',
                  'order.shippingAddress',
                  'shippingOriginAddress',
                  'orderItems',
                  'orderItems.product',
                  'merchant',
                  'merchant.profilePic',
                  'customer',
                  'customer.profilePic',
                  'outboundDeliveries',
                  'returnDeliveries',
                  'shippingRate',
                  'subGroups',
                  'subGroups.orderItems',
                  'subGroups.orderItems.product'
                ].join(',')
              }
            }))
          );
          
          // Check for errors
          const errorResult = results.find(result => result.error);
          if (errorResult) {
            return { error: errorResult.error };
          }
          
          // Return the array of data
          return { data: results.map(result => result.data) };
        } catch (error) {
          return { error: { status: 500, data: error.message } };
        }
      },
      providesTags: (result = [], error, ids) => [
        ...ids.map(id => ({ type: 'FulfillmentGroup', id }))
      ],
    }),

    // Validate an address
    validateAddress: builder.mutation({
      query: (address) => ({
        url: '/shipping/validate-address',
        method: 'POST',
        body: address
      })
    }),

    // Calculate shipping rates
    calculateShippingRates: builder.mutation({
      query: ({ fromAddressId, toAddressId, parcels, merchantUsername, merchantFirstname, merchantLastname, currency, isReturn = false, fromCustomer, insuranceEnabled, insuranceAmount }) => {
        return {
          url: '/shipping-rates',
          method: 'POST',
          body: {
            fromAddressId,
            toAddressId,
            parcels,
            merchantUsername,
            merchantFirstname,
            merchantLastname,
            currency,
            isReturn,
            fromCustomer: fromCustomer || isReturn,
            insuranceEnabled,
            insuranceAmount
          }
        };
      },
      // Add a transformResponse to handle the new response format
      transformResponse: (response) => {

        // Handle the new response format where rates are nested in a rates property
        if (response && response.success && Array.isArray(response.rates)) {
          return { rates: response.rates, addressInfo: response.addressInfo };
        }

        // If it's the old format (just an array of rates), keep backward compatibility
        if (Array.isArray(response)) {
          return { rates: response };
        }

        // If there are no rates or an unexpected format, return empty rates
        return { rates: [], error: response?.message || 'No shipping rates available' };
      }
    }),

    // Get parcel templates available for the region
    // TODO: Backend route and handler for /shipping/parcel-templates needs to be created.
    // getParcelTemplates: builder.query({
    //   query: ({ country = 'US' }) => ({
    //     url: `/shipping/parcel-templates`,
    //     params: { country }
    //   }),
    //   providesTags: ['ParcelTemplate'],
    //   transformResponse: (response) => {
    //     return response.templates || [];
    //   }
    // }),

    // Create a shipping label (updated for Start Shipping Flow)
    createShippingLabel: builder.mutation({
      query: (data) => ({
        url: `/shipping-labels`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, arg) =>
        result?.success
          ? [{ type: 'FulfillmentGroup', id: arg.fulfillmentGroupId }]
          : []
    }),

    // Submit manual tracking info
    createManualTrackingDelivery: builder.mutation({
      query: (data) => ({
        url: `/deliveries/manual-tracking`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, arg) =>
        result?.success && arg.fulfillmentGroupIds
          ? arg.fulfillmentGroupIds.map(id => ({ type: 'FulfillmentGroup', id }))
          : []
    }),

    // Track a shipment
    trackShipment: builder.query({
      query: ({ carrier, trackingNumber }) =>
        // Corrected URL to match backend route definition
        `/track/${carrier}/${trackingNumber}`,
      providesTags: (result, error, { trackingNumber }) => [
        { type: 'Tracking', id: trackingNumber }
      ],
      transformResponse: (response) => {
        if (response && response.success) {
          return response.tracking;
        }
        return null;
      }
    }),

    // Track shipment by tracking number
    trackShipmentByNumber: builder.query({
      query: (trackingNumber) => `/delivery/track/${trackingNumber}`,
      providesTags: (result, error, trackingNumber) => [
        { type: 'Delivery', id: trackingNumber }
      ],
    }),

    // Track return shipment
    trackReturnShipment: builder.query({
      query: (returnTrackingNumber) => `/delivery/track/return/${returnTrackingNumber}`,
      providesTags: (result, error, returnTrackingNumber) => [
        { type: 'Delivery', id: `return_${returnTrackingNumber}` }
      ],
    }),

    // Get tracking info for fulfillment group
    getFulfillmentGroupTracking: builder.query({
      query: (fulfillmentGroupId) => `/fulfillment-groups/${fulfillmentGroupId}/tracking`,
      providesTags: (result, error, fulfillmentGroupId) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'Delivery', id: 'LIST' }
      ],
      transformResponse: (response) => {
        // Transform to provide separate outbound and return tracking
        return {
          fulfillmentGroupId: response.fulfillmentGroupId,
          outboundTracking: response.outboundTracking || [],
          returnTracking: response.returnTracking || []
        };
      },
    }),

    // Get pickup location details
    getPickupLocation: builder.query({
      query: (id) => {
        // Corrected URL to match backend route definition
        return `/pickup-location/${id}`;
      },
      transformResponse: (response) => {
        if (response && response.success && response.location) {
          return response.location;
        }
        return null;
      }
    }),

    // Get all deliveries
    getDeliveries: builder.query({
      query: () => '/deliveries',
      providesTags: (result = [], error, arg) => [
        'Delivery',
        ...result.map(({ id }) => ({ type: 'Delivery', id })),
      ],
    }),

    // Get a single delivery
    getDelivery: builder.query({
      query: (id) => `/deliveries/${id}`,
      providesTags: (result, error, id) => [{ type: 'Delivery', id }],
    }),

    // Update delivery status
    updateDelivery: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/deliveries/${id}`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Delivery', id }],
    }),

    // New mutation to confirm pickup
    confirmPickup: builder.mutation({
      query: ({ fulfillmentGroupId, code }) => ({
        url: `/fulfillment-groups/${fulfillmentGroupId}/confirm-pickup`,
        method: 'POST',
        body: { code },
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
      ],
    }),

    // New mutation to fetch pickup points
    fetchPickupPoints: builder.mutation({
      query: ({ customerAddress, carrierToken, parcelDetails }) => ({
        url: '/shipping/pickup-points', // Matches the backend route we created
        method: 'POST',
        body: {
          customerAddress, // e.g., { street1, city, postalCode, country }
          carrierToken,    // e.g., 'colissimo_fr_pickup'
          parcelDetails    // e.g., { weight, mass_unit } (optional, but good to pass if available)
        }
      }),
      // No invalidation needed as this is a query-like mutation for fetching data
      // transformResponse can be used here if frontend needs a different structure than what backend sends
      transformResponse: (response) => {
        // Assuming backend sends { success: true, points: [...] } based on controller
        if (response && response.success && Array.isArray(response.points)) {
          return response.points;
        }
        // Log unexpected response and return empty array to prevent errors
        console.warn('fetchPickupPoints received unexpected response structure:', response);
        return [];
      },
    }),

    createReturnShippingLabel: builder.mutation({
      query: (data) => {
        return {
          url: `/delivery/create-return-shipping-label`,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: (result, error, arg) => {
        if (!result?.success) return [];

        const tags = [
          { type: 'FulfillmentGroup', id: arg.fulfillmentGroupId },
          { type: 'Request', id: 'LIST' }, // To refresh any request lists that might display return status
          { type: 'Delivery', id: 'LIST' } // Add this to invalidate delivery list cache
        ];

        // If this is an item-level return, also invalidate that specific order item
        if (arg.orderItemId) {
          tags.push({ type: 'OrderItem', id: arg.orderItemId });
        } else {
          // For group-level returns, invalidate all order items generically
          tags.push({ type: 'OrderItem', id: 'LIST' });
        }

        // Add tracking cache invalidation
        if (result.tracking_number) {
          tags.push({ type: 'Tracking', id: result.tracking_number });
        }

        // Also invalidate the fulfillment group tracking query
        tags.push({ type: 'FulfillmentGroup', id: `tracking_${arg.fulfillmentGroupId}` });

        return tags;
      }
    }),

    // Create shipping label (for merchants)
    createShippingLabelForMerchant: builder.mutation({
      query: ({ fulfillmentGroupId, shippingDetails }) => ({
        url: `/delivery/create-label`,
        method: 'POST',
        body: { fulfillmentGroupId, shippingDetails }
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'Delivery', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Create return label (for customers/merchants)
    createReturnLabel: builder.mutation({
      query: ({ fulfillmentGroupId, returnDetails }) => ({
        url: `/delivery/create-return-label`,
        method: 'POST',
        body: { fulfillmentGroupId, returnDetails }
      }),
      invalidatesTags: (result, error, { fulfillmentGroupId }) => [
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'Delivery', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Update delivery status (webhook endpoint)
    updateDeliveryStatus: builder.mutation({
      query: ({ deliveryId, status, trackingDetails }) => ({
        url: `/delivery/${deliveryId}/status`,
        method: 'PUT',
        body: { status, trackingDetails }
      }),
      invalidatesTags: (result, error, { deliveryId, fulfillmentGroupId }) => [
        { type: 'Delivery', id: deliveryId },
        fulfillmentGroupId ? { type: 'FulfillmentGroup', id: fulfillmentGroupId } : undefined,
        { type: 'Order', id: 'LIST' },
      ].filter(Boolean),
    }),

    // Get delivery status
    getDeliveryStatus: builder.query({
      query: (deliveryId) => `/delivery/${deliveryId}/status`,
      providesTags: (result, error, deliveryId) => [
        { type: 'Delivery', id: deliveryId }
      ],
    }),
  }),
});

// Export hooks for the delivery API
export const {
  useGetFulfillmentGroupQuery,
  useGetMultipleFulfillmentGroupsQuery,
  useValidateAddressMutation,
  useCalculateShippingRatesMutation,
  // useGetParcelTemplatesQuery, // Commented out as endpoint is not ready
  useCreateShippingLabelMutation,
  useTrackShipmentQuery,
  useTrackShipmentByNumberQuery,
  useTrackReturnShipmentQuery,
  useGetFulfillmentGroupTrackingQuery,
  useGetPickupLocationQuery,
  useGetDeliveriesQuery,
  useGetDeliveryQuery,
  useUpdateDeliveryMutation,
  useCreateManualTrackingDeliveryMutation,
  useFetchPickupPointsMutation, // Export the new mutation hook
  useConfirmPickupMutation, // Export the new confirm pickup hook
  useCreateReturnShippingLabelMutation, // Export the return shipping label mutation
  useGetDeliveryStatusQuery,
  useCreateShippingLabelForMerchantMutation,
  useCreateReturnLabelMutation,
  useUpdateDeliveryStatusMutation,
} = enhancedDeliveryApi;

export default enhancedDeliveryApi;